import FormLabel from '@mui/material/FormLabel';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';

import { LoadingButton } from '@/components/molecules/LoadingButton';
import { OneOf } from '@/components/molecules/OneOf';
import { ControllerWithError, ControllerWithErrorParam } from '@/forms/ControllerWithError';
import { UseFormWithErrorsReturn } from '@/hooks/useFormWithErrors';
import { useTranslator } from '@/hooks/useTranslator';
import { AddCustomerFormData } from '@/schemas/addCustomerSchema';
import { BeneficiaryType } from '@/types/customer';
import { OneOfOption } from '@/types/oneOfOption';

interface AddCustomerFormProps {
  formMethods: UseFormWithErrorsReturn<AddCustomerFormData>;
  isLoading: boolean;
  onSubmit: () => void | Promise<void>;
}

export const AddCustomerForm: React.FC<AddCustomerFormProps> = ({
  formMethods,
  isLoading,
  onSubmit,
}: AddCustomerFormProps): React.ReactElement => {
  const tr = useTranslator();
  const { register, watch, handleSubmit, formState, control } = formMethods;
  const { isSubmitting } = formState;
  const customerType = watch('customerType');

  const customerTypeOptions = React.useMemo(
    (): readonly OneOfOption[] => [
      {
        label: tr('BUSINESS'),
        value: BeneficiaryType.business,
      },
      {
        label: tr('INDIVIDUAL'),
        value: BeneficiaryType.individual,
      },
    ],
    [tr],
  );

  return (
    <Stack component="form" onSubmit={handleSubmit(onSubmit)} sx={styles.form}>
      <ControllerWithError
        name="customerType"
        label={tr('CUSTOMER_TYPE')}
        control={control}
        render={({
          field,
        }: ControllerWithErrorParam<AddCustomerFormData, 'customerType'>): React.ReactElement => (
          <OneOf options={customerTypeOptions} {...field} />
        )}
      />
      {customerType === BeneficiaryType.business ? (
        <Stack sx={styles.formField}>
          <FormLabel sx={styles.formLabel}>{tr('BUSINESS_INFORMATION')}</FormLabel>
          <TextField
            {...register({ name: 'businessName', label: tr('LEGAL_ENTITY_NAME') })}
            fullWidth
          />
        </Stack>
      ) : (
        <Stack sx={styles.formField}>
          <FormLabel sx={styles.formLabel}>{tr('PERSONAL_INFORMATION')}</FormLabel>
          <Stack direction="row" sx={styles.individualFields}>
            <TextField {...register({ name: 'firstName', label: tr('FIRST_NAME') })} fullWidth />
            <TextField {...register({ name: 'lastName', label: tr('LAST_NAME') })} fullWidth />
          </Stack>
        </Stack>
      )}
      <LoadingButton
        type="submit"
        variant="contained"
        fullWidth
        loading={isLoading}
        disabled={isSubmitting}>
        {tr('START_VERIFICATION')}
      </LoadingButton>
    </Stack>
  );
};

const styles: Record<string, SystemStyleObject> = {
  form: { gap: 6 },
  formField: { gap: 2 },
  formLabel: { fontWeight: 500, fontSize: '16px', color: 'text.primary' },
  individualFields: { gap: 3 },
};
